<template>
  <PlanCard
    :plan-id="plan.id"
    :selectable="selectable"
    :tag-line="tagLine"
    :price-line="priceLine"
    @selected="onPlanSelected">
    <template
      v-if="shouldUseTabletLayout && isCurrent"
      #tagLine>
      (Current)
    </template>
    <template
      v-if="showCustomDisplayName"
      #displayName>
      <h4 class="m-0">
        {{ displayNameCustom }}
      </h4>
    </template>
    <template
      v-if="showPaymentOptions"
      #footer>
      <BaseAccordion
        v-if="membershipPlans"
        title="View Billing Options"
        class="w-100 mt-sm"
        chevron-placement="right"
        :start-expanded="expanded"
        @on-expand="onAccordionExpand">
        <template #title>
          <p class="text-button">
            View Billing Options
          </p>
        </template>
        <div
          class="d-flex flex-column gap-md"
          @click.stop>
          <PlanCardPaymentOption
            v-for="paymentOption in planPaymentOptions"
            :key="paymentOption.id"
            :plan="paymentOption"
            @selected="onPaymentOptionSelected"/>
        </div>
      </BaseAccordion>
    </template>
  </PlanCard>
</template>

<script setup>
import { computed } from 'vue'
import { usePlanSelection } from '@shared/composables/planSelection.js'
import PlanCard from '@shared/components/ADORN/planCard/PlanCard.vue'
import BaseAccordion from '@shared/components/ADORN/BaseAccordion.vue'
import PlanCardPaymentOption from '@shared/components/ADORN/planCard/PlanCardPaymentOption.vue'

const props = defineProps({
  plan: {
    type: Object,
    required: true
  },
  selectable: {
    type: Boolean,
    default: true
  },
  currentPlan: {
    type: String,
    default: null
  },
  startingAtPrice: {
    type: String,
    default: ''
  },
  expanded: {
    type: Boolean,
    default: false
  },
  showPaymentOptions: {
    type: Boolean,
    default: true
  }
})

const { isCurrentPlan, generatePriceLine, membershipPlans, paymentOptionsByNumItems, onPaymentOptionSelected: selectPaymentOption, getCustomDisplayName, shouldShowCustomDisplayName, shouldUseTabletLayout } = usePlanSelection()
const isCurrent = computed(() => isCurrentPlan(props.plan, props.currentPlan))
const priceLine = computed(() => generatePriceLine(props.plan, isCurrent.value, props.startingAtPrice))
const displayNameCustom = computed(() => getCustomDisplayName(props.plan))
const showCustomDisplayName = computed(() => shouldShowCustomDisplayName(props.plan))
const showPaymentOptions = computed(() => props.showPaymentOptions && shouldUseTabletLayout.value)
const planPaymentOptions = computed(() => {
  return paymentOptionsByNumItems(props.plan.nItems) || []
})

const emits = defineEmits(['selected', 'option-selected', 'on-expand'])
// if no tag line, find monthly hero plan (chargeInterval === 1) and use its tag line
const tagLine = computed(() => {
  if (props.plan.tagLine && props.plan.tagLine.length > 0) {
    return props.plan.tagLine
  }
  const paymentOptions = paymentOptionsByNumItems(props.plan.nItems)
  const heroPlan = paymentOptions?.find(plan => plan.chargeInterval === 1)
  return heroPlan?.tagLine || ''
})
function onPlanSelected(planId) {
  emits('selected', planId)
}

function onPaymentOptionSelected(paymentOptionId) {
  selectPaymentOption(paymentOptionId)
  emits('option-selected', paymentOptionId)
}

function onAccordionExpand() {
  emits('on-expand')
}
</script>